// import React from 'react'
import { observable, action, runInAction, makeObservable, IObservableArray } from 'mobx'
import { types as t } from '@invisr/mobx-state-tree'
import errorToast from '../../utils/error-toast'
import fetch from '../../utils/fetch'
import parser from 'fast-xml-parser'
import { promisedComputed } from '../../utils/computed-async-mobx'

export class HANAProvider {
  data = [] as IObservableArray
  config = null
  fieldSet = []

  constructor (config = null, fieldSet = []) {
    makeObservable(this, {
      data: observable
    })

    this.config = config
    this.fieldSet = fieldSet || []
  }

  upsert = async (obj, originalObj = {}) => {}

  delete = obj => {}

  query = async context => {
    const url = `${this.config.baseUrl}/${this.config.service}/${
      this.config.resource
    }?$top=50`

    const results = await fetch('/api/jsonproxy', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        url,
        method: 'GET',
        headers: {
          apikey: this.config.apikey,
          accept: 'application/json',
          'Content-Type': 'application/json'
        }
      })
    }).then(r => r.json())

    runInAction(() =>
      this.data.replace((results && results.d && results.d.results) || [])
    )
    return this.data
  }
}

const resources = async (url, service, apikey) => {
  return fetch('/api/jsonproxy', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      url: `${url}/${service}/$metadata`,
      method: 'GET',
      headers: {
        apikey,
        accept: 'application/xml',
        'Content-Type': 'application/xml'
      }
    })
  })
    .then(r => r.text())
    .then(
      action(result => {
        const res = parser.validate(result)
        if (res !== true) console.log(res.err)
        const json = parser.parse(result, {
          attributeNamePrefix: '',
          parseAttributeValue: true,
          allowBooleanAttributes: false,
          ignoreNameSpace: true,
          ignoreAttributes: false
        })
        const entity = json.Edmx.DataServices.Schema.EntityType
        if (Array.isArray(entity)) return entity
        return [entity]
      })
    )
    .catch(er => errorToast('Error getting Object fields: ', er))
}

export const S4HANA = t
  .model('Salesforce', {
    type: t.optional(t.string, 'Salesforce'),
    service: t.maybeNull(t.string),
    resource: t.maybeNull(t.string),
    apikey: t.maybeNull(t.string),
    baseUrl: t.optional(
      t.string,
      'https://sandbox.api.sap.com/s4hanacloud/sap/opu/odata/sap'
    )
  })
  .views(self => ({
    get services () {
      return SERVICES
    },
    get resources () {
      return (
        self.service &&
        promisedComputed(
          [],
          resources.bind(null, self.baseUrl, self.service, self.apikey)
        )
      )
    },
    getFields (context) {
      return (self as any).fieldSet
    },
    get fieldSet () {
      const resources = (self as any).resources.get() as {Name: string, Property: {Name: string, label: string, Type: string}[]}[]
      const res = resources.find(r => r.Name === self.resource + 'Type')
      if (res) {
        return res.Property.map(f => ({
          fieldPath: f.Name,
          label: f.label,
          type: f.Type.replace('Edm.', '').toUpperCase()
        }))
      }
      return []
    },
    get provider () {
      return HANAProvider
    }
  }))
  .views(self => ({
    async fetchChildNodes () {
      // TODO
    },
    fieldPath ({ meta = { fieldPath: void 0} }) {
      return meta.fieldPath
    },
    labels () {
      const tempProvider = new HANAProvider()
      const functions = Object.keys(tempProvider)
        .filter(k => typeof tempProvider[k] === 'function')
        .map(f => ({
          key: f,
          label: f,
          meta: { fieldPath: f },
          secondaryLabel: 'FUNCTION'
        }))
      const s = self as any
      const labels = {
        key: s.id,
        label: s.name as string,
        secondaryLabel: s.sobject as string,
        childNodes: [
          {
            key: 'methods',
            label: 'Methods',
            childNodes: functions
          },
          {
            key: 'data',
            label: 'Data',
            meta: {
              sobject: s.sobject as string
            },
            asynch: true,
            childNodes: []
          },
          {
            key: 'state',
            label: 'State',
            childNodes: [
              {
                key: 'isLoading',
                label: 'isLoading',
                secondaryLabel: 'state'
              }
            ]
          }
        ]
      }
      return labels
    }
  }))

// TODO: get it from https://api.sap.com/odata/1.0/catalog.svc/ContentEntities.ContentPackages('SAPS4HANACloud')/Artifacts?$format=json&$top=500&$skip=0 (with SubType ODATA)

const SERVICES = [
  {
    value: 'API_SALES_ORDER_WITHOUT_CHARGE_SRV',
    label:
      'Sales Order Without Charge - Create, Read, Update, Delete (A2X) (API_SALES_ORDER_WITHOUT_CHARGE_SRV)'
  },
  {
    value: 'API_RFQ_PROCESS_SRV',
    label: 'Process Requests for Quotation (API_RFQ_PROCESS_SRV)'
  },
  {
    value: 'API_CUSTOMER_RETURN_SRV',
    label:
      'Customer Return - Create, Read, Update, Delete (A2X) (API_CUSTOMER_RETURN_SRV)'
  },
  {
    value: 'API_PURCHASECONTRACT_PROCESS_SRV',
    label: 'Purchase Contract - Read, Create (API_PURCHASECONTRACT_PROCESS_SRV)'
  },
  {
    value: 'API_CUSTOMER_MATERIAL_SRV',
    label:
      'Customer Material - Create, Read, Update, Delete (API_CUSTOMER_MATERIAL_SRV)'
  },
  {
    value: 'API_RAWSUBSTANCE',
    label: 'API for Raw Substance (API_RAWSUBSTANCE)'
  },
  {
    value: 'API_SALES_ORDER_SRV',
    label:
      'Sales Order - Create, Read, Update, Delete (A2X) (API_SALES_ORDER_SRV)'
  },
  {
    value: 'API_MATERIAL_STOCK_SRV',
    label: 'Material Stock Display (API_MATERIAL_STOCK_SRV)'
  },
  {
    value: 'API_SLS_DOC_WITH_CREDIT_BLOCK',
    label:
      'Sales Documents with Credit Blocks – Read, Check, Release, Reject (API_SLS_DOC_WITH_CREDIT_BLOCK)'
  },
  {
    value: 'API_TRSYPOSFLOW_SRV',
    label: 'Treasury Position Flow – Read (API_TRSYPOSFLOW_SRV)'
  },
  {
    value: 'API_BUSINESS_PARTNER',
    label: 'OData Service for Business Partner (API_BUSINESS_PARTNER)'
  },
  {
    value: 'API_DEFECTCATEGORY_SRV',
    label: 'Defect Categories - Read (API_DEFECTCATEGORY_SRV)'
  },
  {
    value: 'API_COSTCENTER_SRV',
    label: 'Cost Center - Read (API_COSTCENTER_SRV)'
  },
  {
    value: '_CPD_SC_EXTERNAL_SERVICES_SRV',
    label: 'Commercial Project - Read (_CPD_SC_EXTERNAL_SERVICES_SRV)'
  },
  {
    value: 'API_MASTERINSPCHARACTERISTIC_SRV',
    label:
      'Master Inspection Characteristics - Read (API_MASTERINSPCHARACTERISTIC_SRV)'
  },
  {
    value: 'API_CUSTOMERSUPPLIERINDUSTRY_SRV',
    label:
      'Read Customer and Supplier Industry (API_CUSTOMERSUPPLIERINDUSTRY_SRV)'
  },
  {
    value: 'API_CREDIT_MEMO_REQUEST_SRV',
    label:
      'Credit Memo Request - Create, Read, Update, Delete (A2X) (API_CREDIT_MEMO_REQUEST_SRV)'
  },
  {
    value: 'API_BILLING_DOCUMENT_SRV',
    label: 'Billing Document - Read, Cancel, GetPDF (API_BILLING_DOCUMENT_SRV)'
  },
  {
    value: 'API_PURCHASING_SOURCE_SRV',
    label: 'Process Purchasing Sources (API_PURCHASING_SOURCE_SRV)'
  },
  {
    value: 'API_INFORECORD_PROCESS_SRV',
    label: 'Process Purchasing InfoRecord (API_INFORECORD_PROCESS_SRV)'
  },
  {
    value: 'API_INSPECTIONMETHOD_SRV',
    label: 'Inspection Methods - Read (API_INSPECTIONMETHOD_SRV)'
  },
  {
    value: 'API_PURCHASEORDER_PROCESS_SRV',
    label:
      'Purchase Order – Create, Update, Delete (API_PURCHASEORDER_PROCESS_SRV)'
  },
  {
    value: 'API_SUPLR_EVAL_RESPONSE_SRV',
    label: 'Process Supplier Evaluation Responses (API_SUPLR_EVAL_RESPONSE_SRV)'
  },
  {
    value: 'API_PLANNED_ORDERS',
    label: 'Planned Orders – Create, Read, Update, Delete (API_PLANNED_ORDERS)'
  },
  {
    value: 'API_CLFN_CHARACTERISTIC_SRV',
    label:
      'Characteristic Data for Classification – Create, Read, Update, Delete (API_CLFN_CHARACTERISTIC_SRV)'
  },
  {
    value: 'API_SALESDISTRICT_SRV',
    label: 'Sales District - Read (API_SALESDISTRICT_SRV)'
  },
  {
    value: 'API_MRP_MATERIALS_SRV_01',
    label: 'Material Planning Data - Read (API_MRP_MATERIALS_SRV_01)'
  },
  {
    value: 'API_CENTRAL_PURCHASECONTRACT_SRV',
    label:
      'Central Purchase Contract – Read, Create (API_CENTRAL_PURCHASECONTRACT_SRV)'
  },
  {
    value: 'API_LEGAL_TRANSACTION_SRV',
    label: 'Legal Transaction (API_LEGAL_TRANSACTION_SRV)'
  },
  {
    value: 'API_RESERVATION_DOCUMENT_SRV',
    label:
      'Reservation Document - Read, Create, Delete (API_RESERVATION_DOCUMENT_SRV)'
  },
  {
    value: 'API_PROD_ORDER_CONFIRMATION',
    label: 'Read Production Order Confirmations (API_PROD_ORDER_CONFIRMATION)'
  },
  {
    value: 'API_DEL_DOC_WITH_CREDIT_BLOCK',
    label:
      'Delivery Documents with Credit Blocks – Read, Check, Release, Reject (API_DEL_DOC_WITH_CREDIT_BLOCK)'
  },
  {
    value: 'API_PURGPRICINGPROCEDURE_SRV',
    label:
      'Pricing Procedure in Purchasing – Read (API_PURGPRICINGPROCEDURE_SRV)'
  },
  {
    value: 'API_CLFN_CLASS_SRV',
    label:
      'Class Data for Classification – Create, Read, Update, Delete (API_CLFN_CLASS_SRV)'
  },
  {
    value: 'API_FINPLANNINGENTRYITEM_SRV',
    label: 'Read Financial Planning Entry Items (API_FINPLANNINGENTRYITEM_SRV)'
  },
  { value: 'API_SEGMENT_SRV', label: 'Read Segment (API_SEGMENT_SRV)' },
  {
    value: 'API_PRODUCTION_ORDERS',
    label: 'Production Order - Read, Create, Update (API_PRODUCTION_ORDERS)'
  },
  {
    value: 'API_SERVICE_ENTRY_SHEET_SRV',
    label: 'Service Entry Sheets – Read, Write (API_SERVICE_ENTRY_SHEET_SRV)'
  },
  {
    value: 'API_PURCHASING_CATEGORY_SRV',
    label: 'Process Purchasing Categories (API_PURCHASING_CATEGORY_SRV)'
  },
  {
    value: 'API_COMPANYCODE_SRV',
    label: 'Read Company Code (API_COMPANYCODE_SRV)'
  },
  { value: 'API_DIVISION_SRV', label: 'Division - Read (API_DIVISION_SRV)' },
  {
    value: 'API_BILL_OF_MATERIAL_SRV',
    label: 'Bills of Material - Read,Create,Update (API_BILL_OF_MATERIAL_SRV)'
  },
  { value: 'API_BANKDETAIL_SRV', label: 'Bank - Read (API_BANKDETAIL_SRV)' },
  {
    value: 'API_PLANNINGCATEGORY_SRV',
    label: 'Plan Category - Read (API_PLANNINGCATEGORY_SRV)'
  },
  {
    value: 'API_PURCHASEREQ_PROCESS_SRV',
    label:
      'Purchase Requisition - Read, Create, Update, Delete (API_PURCHASEREQ_PROCESS_SRV)'
  },
  {
    value: 'API_PRODUCT_ALLOCATION_OBJECT_SRV',
    label:
      'API for Product Allocation Object (API_PRODUCT_ALLOCATION_OBJECT_SRV)'
  },
  {
    value: 'API_PROFITCENTER_SRV',
    label: 'Profit Center - Read (API_PROFITCENTER_SRV)'
  },
  {
    value: 'API_SLSPRICINGPROCEDURE_SRV',
    label: 'Pricing Procedure in Sales - Read (API_SLSPRICINGPROCEDURE_SRV)'
  },
  {
    value: 'API_SALES_INQUIRY_SRV',
    label: 'Sales Inquiry - Read (A2X) (API_SALES_INQUIRY_SRV)'
  },
  {
    value: 'API_INSPECTIONLOT_SRV',
    label: 'Inspection Lot – Read, Write (API_INSPECTIONLOT_SRV)'
  },
  {
    value: 'API_BUSINESSAREA_SRV',
    label: 'Read Business Area (API_BUSINESSAREA_SRV)'
  },
  {
    value: 'API_CUSTOMERGROUP_SRV',
    label: 'Read Customer Group (API_CUSTOMERGROUP_SRV)'
  },
  {
    value: 'API_SUPPLIER_ACTIVITY_SRV',
    label: 'Process Supplier Activities (API_SUPPLIER_ACTIVITY_SRV)'
  },
  {
    value: 'CA_BEH_SUBSCRIPTION_SRV',
    label: 'Business Events - Subscription Service (CA_BEH_SUBSCRIPTION_SRV)'
  },
  { value: 'API_COUNTRY_SRV', label: 'Read Country (API_COUNTRY_SRV)' },
  {
    value: 'API_FUNCTIONALAREA_SRV',
    label: 'Functional Area - Read (API_FUNCTIONALAREA_SRV)'
  },
  {
    value: 'API_CONTROLLINGAREA_SRV',
    label: 'Controlling Area - Read (API_CONTROLLINGAREA_SRV)'
  },
  {
    value: 'API_DEFECT_SRV',
    label: 'Defect - Create, Read, Update, Delete (API_DEFECT_SRV)'
  },
  {
    value: 'API_REALSUBSTANCE',
    label: 'API for Real Substance (API_REALSUBSTANCE)'
  },
  {
    value: 'API_SLSPRICINGCONDITIONRECORD_SRV',
    label:
      'Sales Pricing Condition Record – Read (API_SLSPRICINGCONDITIONRECORD_SRV)'
  },
  {
    value: 'API_BOM_WHERE_USED_SRV',
    label: 'Bills of Material Where-Used List - Read (API_BOM_WHERE_USED_SRV)'
  },
  {
    value: 'API_PROC_ORDER_CONFIRMATION',
    label: 'Read Process Order Confirmations (API_PROC_ORDER_CONFIRMATION)'
  },
  {
    value: 'API_PARTNERCOMPANY_SRV',
    label: 'Read Trading Partner (API_PARTNERCOMPANY_SRV)'
  },
  {
    value: 'API_PRODUCT_SRV',
    label: 'Product Master - Create, Read, Update, Delete (API_PRODUCT_SRV)'
  },
  {
    value: 'API_PUR_QUOTA_ARRANGEMENT_SRV',
    label:
      'Quota Arrangement - Read, Create, Update, Delete (API_PUR_QUOTA_ARRANGEMENT_SRV)'
  },
  {
    value: 'API_MANAGE_SKILLTAGS_SRV',
    label:
      'Workforce Person SkillTag – Create, Read and Delete (API_MANAGE_SKILLTAGS_SRV)'
  },
  {
    value: 'API_TRSYPOSTGJRNLENTRITM_SRV',
    label:
      'Line Item of Treasury Posting Journal Entry – Read (API_TRSYPOSTGJRNLENTRITM_SRV)'
  },
  {
    value: '_CPD_SC_PROJ_ENGMT_CREATE_UPD_SRV',
    label:
      'Create and Update Commercial Projects (_CPD_SC_PROJ_ENGMT_CREATE_UPD_SRV)'
  },
  {
    value: 'API_SUPPLIEROPLSCORESAV_CDS',
    label: 'Supplier Evaluation Score - Read (API_SUPPLIEROPLSCORESAV_CDS)'
  },
  {
    value: 'API_CHANGE_RECORD',
    label: 'Change Record - Read, Create (API_CHANGE_RECORD)'
  },
  {
    value: 'API_PLND_INDEP_RQMT_SRV',
    label:
      'Planned Independent Reqmts for Use with Change PIRs App - Create, Read, Update (API_PLND_INDEP_RQMT_SRV)'
  },
  {
    value: 'API_SLSPRICINGCONDITIONTYPE_SRV',
    label:
      'Condition Type for Pricing in Sales – Read (API_SLSPRICINGCONDITIONTYPE_SRV)'
  },
  {
    value: 'API_KANBAN_CONTROL_CYCLE_SRV',
    label: 'Kanban Control Cycle – Read, Write (API_KANBAN_CONTROL_CYCLE_SRV)'
  },
  {
    value: 'API_SALES_CONTRACT_SRV',
    label:
      'Sales Contract - Create, Read, Update, Delete (A2X) (API_SALES_CONTRACT_SRV)'
  },
  {
    value: 'API_PRODUCTGROUP_SRV',
    label: 'Product Group - Read (API_PRODUCTGROUP_SRV)'
  },
  {
    value: 'API_BILL_OF_MATERIAL_SRV_0002',
    label:
      'Bills of Material - Read, Create, Update, Delete (Version 2) (API_BILL_OF_MATERIAL_SRV_0002)'
  },
  {
    value: 'API_DEFECTCODE_SRV',
    label: 'Defect Codes and Code Groups - Read (API_DEFECTCODE_SRV)'
  },
  {
    value: 'API_SALES_QUOTATION_SRV',
    label:
      'Sales Quotation - Create, Read, Update, Delete (A2X) (API_SALES_QUOTATION_SRV)'
  },
  {
    value: 'API_PRODUCTIONSUPPLYAREA_SRV',
    label: 'Production Supply Area – Read, Write (API_PRODUCTIONSUPPLYAREA_SRV)'
  },
  { value: 'API_LEDGER_SRV', label: 'Read Ledger (API_LEDGER_SRV)' },
  {
    value: 'API_DISTRIBUTIONCHANNEL_SRV',
    label: 'Distribution Channel - Read (API_DISTRIBUTIONCHANNEL_SRV)'
  },
  {
    value: 'API_RECIPE',
    label: 'Recipe Header, Formula, and Process – Read, Write (API_RECIPE)'
  },
  {
    value: 'API_CLFN_PRODUCT_SRV',
    label:
      'Product Master Data Including Classification – Read (API_CLFN_PRODUCT_SRV)'
  },
  {
    value: 'API_OUTBOUND_DELIVERY_SRV',
    label: 'Outbound Deliveries - Read, Write (API_OUTBOUND_DELIVERY_SRV)'
  },
  {
    value: 'API_CV_ATTACHMENT_SRV',
    label: 'Attachment - Create, Read, Rename, Delete (API_CV_ATTACHMENT_SRV)'
  },
  {
    value: 'API_QTN_PROCESS_SRV',
    label: 'Process Supplier Quotations (API_QTN_PROCESS_SRV)'
  },
  {
    value: 'C_TRIALBALANCE_CDS',
    label: 'Read Trial Balance (C_TRIALBALANCE_CDS)'
  },
  {
    value: 'API_MANAGE_WORKFORCE_TIMESHEET',
    label: 'Manage Workforce Timesheet (API_MANAGE_WORKFORCE_TIMESHEET)'
  },
  {
    value: 'API_SUPPLIER_ACTIVITY_TASK_SRV',
    label: 'Process Supplier Activty Tasks (API_SUPPLIER_ACTIVITY_TASK_SRV)'
  },
  {
    value: 'API_LEGAL_DOCUMENT_SRV',
    label: 'Legal Document (API_LEGAL_DOCUMENT_SRV)'
  },
  {
    value: 'API_DMS_PROCESS_SRV',
    label: 'Document Info record – Create, Read, Update (API_DMS_PROCESS_SRV)'
  },
  {
    value: 'API_GLACCOUNTINCHARTOFACCOUNTS_SRV',
    label: 'Read G/L Account (API_GLACCOUNTINCHARTOFACCOUNTS_SRV)'
  },
  {
    value: 'API_CUSTOMER_RETURNS_DELIVERY_SRV',
    label:
      'Customer Returns Deliveries – Read, Create, Update, and Delete (API_CUSTOMER_RETURNS_DELIVERY_SRV)'
  },
  {
    value: 'API_INBOUND_DELIVERY_SRV',
    label:
      'Inbound Deliveries – Read, Create, Update, and Delete (API_INBOUND_DELIVERY_SRV)'
  },
  {
    value: 'API_FORECAST_DEMAND_SRV',
    label:
      'Planned Independent Reqmts for Use with Manage PIRs App - Read, Update (API_FORECAST_DEMAND_SRV)'
  },
  { value: 'API_WORK_CENTERS', label: 'Work Centers (API_WORK_CENTERS)' },
  {
    value: 'FCO_PI_COST_CENTER',
    label: 'Read Cost Center Data (FCO_PI_COST_CENTER)'
  },
  {
    value: 'API_SUPPLIERINVOICE_PROCESS_SRV',
    label:
      'Supplier Invoice – Create, Read, Release, Reverse (API_SUPPLIERINVOICE_PROCESS_SRV)'
  },
  {
    value: 'API_JOURNALENTRYITEMBASIC_SRV',
    label: 'Read Journal Entry Items (API_JOURNALENTRYITEMBASIC_SRV)'
  },
  {
    value: 'API_CHARTOFACCOUNTS_SRV',
    label: 'Read Chart of Accounts (API_CHARTOFACCOUNTS_SRV)'
  },
  {
    value: 'API_BILLING_DOCUMENT_REQUEST_SRV',
    label:
      'Billing Document Request - Read, Reject, Delete (API_BILLING_DOCUMENT_REQUEST_SRV)'
  },
  {
    value: 'API_CONDITION_CONTRACT_TYPE',
    label: 'Condition Contract Type - Read (API_CONDITION_CONTRACT_TYPE)'
  },
  {
    value: 'API_CONTROLLINGDEBITCREDITCODE_SRV',
    label:
      'Debit/Credit Indicator CO - Read (API_CONTROLLINGDEBITCREDITCODE_SRV)'
  },
  {
    value: 'API_SUPLR_EVAL_SCORECARD_SRV',
    label:
      'Process Supplier Evaluation Scorecards (API_SUPLR_EVAL_SCORECARD_SRV)'
  },
  {
    value: 'API_MANAGE_WF_AVAILABILITY',
    label: 'Manage Workforce Availability (API_MANAGE_WF_AVAILABILITY)'
  },
  {
    value: 'API_MATERIAL_VALUATION_SRV',
    label: 'Update of Material Price (API_MATERIAL_VALUATION_SRV)'
  },
  {
    value: 'API_PHYSICAL_INVENTORY_DOC_SRV',
    label:
      'Process Physical Inventory Documents (API_PHYSICAL_INVENTORY_DOC_SRV)'
  },
  {
    value: 'API_BILLOFMATERIAL_COMPARISON_SRV',
    label: 'Bill of Material Comparison (API_BILLOFMATERIAL_COMPARISON_SRV)'
  },
  {
    value: 'API_SCHED_AGRMT_PROCESS_SRV',
    label: 'Scheduling Agreements – Create, Read (API_SCHED_AGRMT_PROCESS_SRV)'
  },
  {
    value: 'BC_EXT_APPJOB_MANAGEMENT',
    label:
      'Enable External Job Scheduler Integration (BC_EXT_APPJOB_MANAGEMENT)'
  },
  {
    value: 'API_CHANGEMASTER',
    label: 'Create, Read, Update and Delete Change Masters (API_CHANGEMASTER)'
  },
  {
    value: 'API_MATERIAL_DOCUMENT_SRV',
    label: 'Process Material Documents (API_MATERIAL_DOCUMENT_SRV)'
  },
  {
    value: 'API_PRODUCT_AVAILY_INFO_BASIC',
    label:
      'Basic Product Availability Information API (API_PRODUCT_AVAILY_INFO_BASIC)'
  },
  {
    value: 'API_DEFECTCLASS_SRV',
    label: 'Defect Classes - Read (API_DEFECTCLASS_SRV)'
  },
  {
    value: 'API_COSTCENTERACTIVITYTYPE_SRV',
    label: 'Activity Type - Read (API_COSTCENTERACTIVITYTYPE_SRV)'
  },
  {
    value: 'API_SALESORGANIZATION_SRV',
    label: 'Sales Organization - Read (API_SALESORGANIZATION_SRV)'
  },
  {
    value: 'API_PROCESS_ORDERS',
    label: 'Read Process Orders (API_PROCESS_ORDERS)'
  },
  {
    value: 'API_PRODUCT_ALLOC_SEQUENCE_SRV',
    label:
      'API for Product Allocation Sequence (API_PRODUCT_ALLOC_SEQUENCE_SRV)'
  },
  {
    value: 'API_STATISTICALKEYFIGURE_SRV',
    label:
      'Statistical Key Figure - Create, Read, Update, Delete (API_STATISTICALKEYFIGURE_SRV)'
  },
  {
    value: 'API_DEBIT_MEMO_REQUEST_SRV',
    label:
      'Debit Memo Request - Create, Read, Update, Delete (A2X) (API_DEBIT_MEMO_REQUEST_SRV)'
  },
  {
    value: 'C_BEHQUEUEDATA_CDS',
    label: 'Business Event Queue (C_BEHQUEUEDATA_CDS)'
  },
  {
    value: 'API_OPLACCTGDOCITEMCUBE_SRV',
    label: 'Accounting Document - Read (API_OPLACCTGDOCITEMCUBE_SRV)'
  },
  {
    value: 'API_PURGPRICINGCONDITIONTYPE_SRV',
    label:
      'Condition Type for Pricing in Purchasing – Read (API_PURGPRICINGCONDITIONTYPE_SRV)'
  },
  {
    value: 'API_ENTERPRISE_PROJECT_SRV',
    label:
      'Enterprise Project - Create, Read, Update, Delete (API_ENTERPRISE_PROJECT_SRV)'
  },
  {
    value: 'API_BUSINESS_SITUATION_SRV',
    label: 'Business Situation - Read (API_BUSINESS_SITUATION_SRV)'
  }
]

export default S4HANA
